import React, { useState, useEffect, useRef, useCallback } from 'react';
import _ from 'lodash';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Button,
  Grid,
  CircularProgress,
  Divider,
  Typography,
  TextField,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

import { useQuery, useLazyQuery, useMutation, useApolloClient } from '@apollo/client';
import i18next from 'i18next';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { PDF_IMPORT_ENABLED_QUERY } from './pdf-import';
import { PDF_IMPORT_TARGET_CLOSED_STATUSES } from 'semshared/offer/pdf-import';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleIcon from '@mui/icons-material/People';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fab from '@mui/material/Fab';

import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { getFullBackendUrl } from 'helper/download';
import { bookingModeSelector } from 'helper/filter';
import { EBookingMode } from 'types';

import { CustomTabsControlled } from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';
import BookingTasksList from 'components/BookingTasksList';

import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import AgbConfirmationDialog from 'components/dialogs/AgbConfirmationDialog';
import CustomerCommentDialog from 'components/dialogs/CustomerCommentDialog';

import {
  OFFERVERSION_APPROVE_MUTATION,
  OFFERVERSION_REJECT_MUTATION,
  OFFERVERSION_CONFIRM_CANCELLATION_MUTATION,
  OFFER_VIEW_QUERY,
  OFFER_AND_VERSION_VIEW_QUERY,
  OFFERVERSION_VIEW_CANCELLATION_RULES_QUERY,
  REFETCH_OFFER_QUERIES,
  EVICT_OFFER_QUERIES,
  OFFERVERSION_PROCESSEVENTSTARTED_MUTATION,
  OFFERVERSION_PROCESSEVENTENDED_MUTATION,
  EVENT_VIEW_QUERY,
  OFFERVERSION_UPSERT_MUTATION,
  OFFERVERSION_RESET_MUTATION,
  RESEND_OFFER_EMAIL_CLIENT_MUTATION,
} from './gql';
import {
  INTEGRATION_SYNCBOOKING_MUTATION,
  INTEGRATION_CONFIRMBOOKING_MUTATION,
  INTEGRATION_DELETEBOOKING_MUTATION,
  INTEGRATION_GROUPBOOKINGINFO_VIEW_QUERY,
  EVICT_INTEGRATION_HOTELLOG_QUERIES,
} from '../settings/integration/gql';

import {
  AMEventDetailsOutputFragment,
  AMEventOutput,
  EAMRoomType,
  EContentOfferTemplateType,
  EOfferProcess,
  EOfferRoomSeating,
  EOfferSource,
  EOfferStatus,
  EOfferVersionContentBlockFormFieldType,
  EOfferVersionContentBlockType,
  EOfferVersionStatus,
  EOfferVersionType,
  ViewWlOfferQuery,
  ViewWlOfferVersionQuery,
  WLOfferListOutput,
  WLOfferListViewFragment,
  WLOfferVersionDiffOutputFragment,
  WLOfferVersionListOutput,
  WLOfferVersionListViewFragment,
} from '__generated__/graphql';

import OfferVersion from './editor';
import OfferDuplicateButton from './OfferDuplicateButton';
import SimpleAccordion from 'components/SimpleAccordion';
import AdminOfferDocumentViewer from 'components/AdminOfferDocumentViewer';
import { FormatDate, FormatDateTime, formatDate } from 'components/DateTime';

import { IntegrationLogsTable } from '../settings/integration/logs';
import InformationDialog from 'components/dialogs/InformationDialog';
import SimpleDialog from 'components/dialogs/Dialog';
import OfferHistoryTable from 'components/OfferHistoryTable';
import AddonChangesSummary, { hasRecentAddonRemovals } from 'components/AddonChangesSummary';

import { canBookingOpenPMS, canBookingSendPMS, canBookingConfirmPMS, canBookingDeletePMS } from 'pages/settings/integration/pmssupport';
import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';
import { NO_PRICE } from 'semshared/pricelist/quickprice';
import { sha256Hex, userSelector, buildGuestOfferUser, GuestOfferUserContext } from 'helper/security';
import { formatOfferDay } from 'semshared/utils/format';
import { SubscribeToOfferChange } from './subscription';
import { OfferVersionFormType } from 'components/template_editor/context';
import OfferInputView from 'components/OfferInputView';
import { OfferRejectionDialog } from 'components/OfferRejectionDialog';
import { EOfferRejectionReason } from '__generated__/graphql';
import OfferActionMenu from 'components/OfferActionMenu';
import ContactPersonTabsWrapper from 'components/ContactPersonTabsWrapper';

function useBookingModeRedirect(bookingMode: EBookingMode | null, offerSource: EOfferSource | undefined): string | null {
  if (!bookingMode || !offerSource) return null;
  const expectedMode = offerSource === EOfferSource.WIDGET ? EBookingMode.SEM : EBookingMode.EPILO;
  if (bookingMode === expectedMode) return null;
  if (bookingMode === EBookingMode.SEM) return '/offers/widget';
  if (bookingMode === EBookingMode.EPILO) return '/offers/lister';
  return null;
}

interface OfferProps {
  id: number;
  versionId: number;
}

interface OfferLatestProps {
  id: number;
  guestMode?: boolean;
}

interface OfferFormProps {
  data: WLOfferListViewFragment;
  version: WLOfferVersionListViewFragment & WLOfferVersionDiffOutputFragment;
  event: AMEventDetailsOutputFragment | null;
  guestMode?: boolean;
}

function ScrollToTopButton() {
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const checkScrollTop = () => {
      const mainElement = document.querySelector('main');
      if (mainElement && mainElement.scrollTop > 300) {
        setShowScroll(true);
      } else {
        setShowScroll(false);
      }
    };

    const mainElement = document.querySelector('main');
    mainElement?.addEventListener('scroll', checkScrollTop);

    return () => {
      mainElement?.removeEventListener('scroll', checkScrollTop);
    };
  }, []);

  const scrollTop = () => {
    const mainElement = document.querySelector('main');
    mainElement?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!showScroll) return null;

  return (
    <Fab
      color="primary"
      size="medium"
      onClick={scrollTop}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 200,
        zIndex: 1000,
      }}
    >
      <KeyboardArrowUpIcon />
    </Fab>
  );
}

function StableInlineText({ children }: { children: React.ReactNode }) {
  return <Box component="span">{children}</Box>;
}

function InlineIconText({ children, icon }: { children: React.ReactNode; icon: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <StableInlineText>{children}</StableInlineText>
      {icon}
    </Box>
  );
}

// Utility: Extract line items with price/count fields only for CR change detection
function extractCrLineItemsData(values: OfferVersionFormType): string {
  const lineItems = (values.contentBlocks ?? [])
    .flatMap(cb => cb.lineItems ?? [])
    .filter(Boolean)
    .map(li => ({
      day: li.day,
      count: li.count,
      priceItem: li.priceItem,
      priceNet: li.priceNet,
      priceGross: li.priceGross,
      components: (li.components ?? []).map(c => ({ taxTypeId: c.taxTypeId, price: c.price })),
      taxes: (li.taxes ?? []).map(t => ({ taxRateId: t.taxRateId, price: t.price })),
    }));
  return JSON.stringify(lineItems);
}

function OfferForm(props: OfferFormProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxUser = userSelector();
  const user = props.guestMode ? buildGuestOfferUser(props.data.space) : reduxUser!;
  const client = useApolloClient();
  const bookingMode = props.guestMode ? null : bookingModeSelector();

  const [upsertMutateFunction] = useMutation(OFFERVERSION_UPSERT_MUTATION);
  const [approveOfferVersion] = useMutation(OFFERVERSION_APPROVE_MUTATION);
  const [rejectOfferVersion] = useMutation(OFFERVERSION_REJECT_MUTATION);
  const [processEventStartedMutateFunction, { loading: processEventStartedMutateLoading }] = useMutation(OFFERVERSION_PROCESSEVENTSTARTED_MUTATION);
  const [processEventEndedMutateFunction, { loading: processEventEndedMutateLoading }] = useMutation(OFFERVERSION_PROCESSEVENTENDED_MUTATION);
  const [syncMutateFunction, { loading: syncMutateLoading }] = useMutation(INTEGRATION_SYNCBOOKING_MUTATION);
  const [confirmMutateFunction, { loading: confirmMutateLoading }] = useMutation(INTEGRATION_CONFIRMBOOKING_MUTATION);
  const [deleteMutateFunction, { loading: deleteMutateLoading }] = useMutation(INTEGRATION_DELETEBOOKING_MUTATION);
  const [viewInfoQuery, { loading: viewInfoLoading }] = useLazyQuery(INTEGRATION_GROUPBOOKINGINFO_VIEW_QUERY);
  const [resetOfferVersion] = useMutation(OFFERVERSION_RESET_MUTATION);
  const [resendOfferEmailClient, { loading: resendOfferEmailLoading }] = useMutation(RESEND_OFFER_EMAIL_CLIENT_MUTATION);
  const [confirmCancellationHotel] = useMutation(OFFERVERSION_CONFIRM_CANCELLATION_MUTATION);

  const offerSource = props.data.source;
  const offerStatus = props.data.status;
  const versionStatus = props.version.status;
  const versionType = props.version.type;

  const canConfirmCancellation = versionStatus === EOfferVersionStatus.CANCELLED_CLIENT_PENDING_HOTEL;

  const canDirectApprove =
    (versionType === EOfferVersionType.OFFER &&
      offerStatus === EOfferStatus.PENDING &&
      (versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL || versionStatus === EOfferVersionStatus.WAITFORHOTELRESERVATION)) ||
    (versionType === EOfferVersionType.OFFER &&
      offerStatus === EOfferStatus.PENDING &&
      versionStatus === EOfferVersionStatus.WAITFORCLIENTAPPROVAL &&
      (user.isSeminargo || offerSource === EOfferSource.WIDGET)) ||
    (versionType === EOfferVersionType.EDITING &&
      offerStatus === EOfferStatus.PENDING &&
      versionStatus === EOfferVersionStatus.WAITFORCLIENTAPPROVAL &&
      (user.isSeminargo || offerSource === EOfferSource.WIDGET)) ||
    (versionType === EOfferVersionType.EDITING &&
      (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING) &&
      (versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.WAITFORHOTELRESERVATION)) ||
    (versionType === EOfferVersionType.OFFER &&
      (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING) &&
      versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) ||
    (versionType === EOfferVersionType.OFFER &&
      (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING) &&
      versionStatus === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL &&
      (user.isSeminargo || offerSource === EOfferSource.WIDGET)) ||
    (versionType === EOfferVersionType.OFFER &&
      offerStatus === EOfferStatus.PENDING &&
      versionStatus === EOfferVersionStatus.WAITFORSEMAPPROVAL &&
      user.isSeminargo);

  const canReject =
    (versionType === EOfferVersionType.OFFER &&
      offerStatus === EOfferStatus.PENDING &&
      (versionStatus == EOfferVersionStatus.WAITFORCLIENTAPPROVAL ||
        versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.WAITFORHOTELRESERVATION)) ||
    (versionType === EOfferVersionType.EDITING &&
      offerStatus === EOfferStatus.PENDING &&
      versionStatus === EOfferVersionStatus.WAITFORCLIENTAPPROVAL) ||
    (versionType === EOfferVersionType.EDITING &&
      (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING) &&
      (versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.WAITFORHOTELRESERVATION)) ||
    (versionType === EOfferVersionType.OFFER &&
      (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING) &&
      versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) ||
    (versionType === EOfferVersionType.OFFER &&
      (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING) &&
      versionStatus === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL);

  const canChange =
    (versionType === EOfferVersionType.OFFER && offerStatus === EOfferStatus.PENDING && versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL) ||
    (versionType === EOfferVersionType.OFFER &&
      offerStatus === EOfferStatus.PENDING &&
      versionStatus === EOfferVersionStatus.WAITFORCLIENTAPPROVAL &&
      (user.isSeminargo || offerSource === EOfferSource.WIDGET)) ||
    (versionType === EOfferVersionType.RESERVATION_CONFIRMATION &&
      offerStatus === EOfferStatus.APPROVED &&
      versionStatus === EOfferVersionStatus.APPROVED) ||
    (versionType === EOfferVersionType.EDITING && (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING)) ||
    (versionType === EOfferVersionType.OFFER &&
      (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING) &&
      versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) ||
    (versionType === EOfferVersionType.OFFER &&
      (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.PENDING) &&
      versionStatus === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL &&
      (user.isSeminargo || offerSource === EOfferSource.WIDGET)) ||
    (versionType === EOfferVersionType.OFFER &&
      offerStatus === EOfferStatus.PENDING &&
      versionStatus === EOfferVersionStatus.WAITFORSEMAPPROVAL &&
      user.isSeminargo);

  const canCancel =
    offerSource === EOfferSource.WIDGET &&
    versionType === EOfferVersionType.RESERVATION_CONFIRMATION &&
    offerStatus === EOfferStatus.APPROVED &&
    versionStatus === EOfferVersionStatus.APPROVED;

  const canDownload =
    (versionType === EOfferVersionType.OFFER &&
      offerStatus !== EOfferStatus.REJECTED &&
      versionStatus !== EOfferVersionStatus.REJECTED_HOTEL_WITHDRAWN) ||
    (versionType === EOfferVersionType.RESERVATION_CONFIRMATION &&
      offerStatus === EOfferStatus.APPROVED &&
      versionStatus === EOfferVersionStatus.APPROVED) ||
    (versionType === EOfferVersionType.RESERVATION_CONFIRMATION &&
      offerStatus === EOfferStatus.CLOSED &&
      (versionStatus === EOfferVersionStatus.CANCELLED_CLIENT_PENDING_HOTEL || versionStatus === EOfferVersionStatus.CANCELLED_CLIENT)) ||
    // SEM cancellation creates an INVOICE-type version with CANCELLED_CLIENT status
    (versionType === EOfferVersionType.INVOICE && versionStatus === EOfferVersionStatus.CANCELLED_CLIENT);

  /** Offer-level APPROVED, or classic cancel flow (offer often CLOSED while version holds cancel state). */
  const useReservationConfirmationDownloadLabel =
    offerStatus === EOfferStatus.APPROVED ||
    versionStatus === EOfferVersionStatus.CANCELLED_CLIENT_PENDING_HOTEL ||
    versionStatus === EOfferVersionStatus.CANCELLED_CLIENT;

  const useCancellationConfirmationDownloadLabel = versionStatus === EOfferVersionStatus.CANCELLED_CLIENT;

  const canSaveChanges =
    canChange &&
    offerSource === EOfferSource.WIDGET &&
    (offerStatus === EOfferStatus.PENDING || offerStatus === EOfferStatus.APPROVED) &&
    (versionType === EOfferVersionType.EDITING ||
      versionType === EOfferVersionType.OFFER ||
      versionType === EOfferVersionType.RESERVATION_CONFIRMATION);

  // For EPILO to check if the offer can be saved.
  const canSaveChangesEPILO =
    canChange &&
    offerSource === EOfferSource.LISTER &&
    offerStatus === EOfferStatus.PENDING &&
    versionType === EOfferVersionType.OFFER &&
    (versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL ||
      versionStatus === EOfferVersionStatus.WAITFORCLIENTAPPROVAL ||
      versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
      versionStatus === EOfferVersionStatus.WAITFORSEMAPPROVAL);

  // For SEM bookings with APPROVED status, no "Entwurf löschen" - only save and send
  const canResetChanges =
    canChange && offerSource === EOfferSource.WIDGET && versionType === EOfferVersionType.EDITING && offerStatus !== EOfferStatus.APPROVED;

  // A new inquiry is when the hotel hasn't yet sent the offer to the client
  const isNewInquiry =
    offerSource === EOfferSource.WIDGET &&
    versionType === EOfferVersionType.OFFER &&
    (versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL || versionStatus === EOfferVersionStatus.WAITFORHOTELRESERVATION);

  const [hasNoPrices] = useState(
    props.version.totalPriceGross === NO_PRICE ||
      !!props.version.contentBlocks
        .filter(cb => cb.type === EOfferVersionContentBlockType.LINE_ITEMS)
        .find(cb => cb.lineItems.find(li => li.priceItem === NO_PRICE)),
  );
  // For SEM bookings with RESERVATION_CONFIRMATION + APPROVED, start in view mode so "Reservierungsbestätigung ändern" button is shown
  const [offerPeriod, setOfferPeriod] = useState<{ startDate: string; endDate: string } | null>(null);
  const [editing, setEditing] = useState(
    canChange &&
      !(
        offerSource === EOfferSource.WIDGET &&
        versionType === EOfferVersionType.RESERVATION_CONFIRMATION &&
        versionStatus === EOfferVersionStatus.APPROVED
      ),
  );
  useEffect(() => {
    setOfferPeriod(null);
  }, [editing, props.version.id]);
  const handleOfferPeriodChange = useCallback((period: { startDate: string; endDate: string } | null) => {
    setOfferPeriod(prev => {
      if (!period) return prev === null ? prev : null;
      if (prev?.startDate === period.startDate && prev?.endDate === period.endDate) return prev;
      return period;
    });
  }, []);
  const [selectedTab, setSelectedTab] = React.useState(
    location.hash === '#event'
      ? 4
      : location.hash === '#history'
        ? 5
        : location.hash === '#editor' || editing
          ? 1
          : hasNoPrices
            ? 1
            : props.version.status === EOfferVersionStatus.WAITFORHOTELAPPROVAL
              ? 1
              : props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL && props.version.type === EOfferVersionType.EDITING
                ? 1
                : props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL && props.version.type === EOfferVersionType.OFFER
                  ? 1
                  : props.version.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL && props.version.type === EOfferVersionType.OFFER
                    ? 1
                    : 0,
  );
  const [logUpdateCounter, setLogUpdateCounter] = useState(0);
  const [viewExtRefData, setViewExtRefData] = useState<string | null | undefined>(null);

  // const [editing, setEditing] = useState(canChange && (hasNoPrices || props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL));
  // Ref keeps savePrices in sync for upsert (confirm dialog updates this right before submit).
  const savePricesRef = useRef(true);
  //const [rejectReason, setRejectReason] = useState<string | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [canEditorSave, setCanEditorSave] = useState(false);
  const [isEditorValid, setIsEditorValid] = useState(true);
  const [submitTrigger, setSubmitTrigger] = useState(0);
  const [submitProcess, setSubmitProcess] = useState(true);
  const lastEditorSubmitWasApproveRef = useRef(true);
  const [submitAndSendEmail, setSubmitAndSendEmail] = useState(false);
  const [initialEditorValuesHash, setInitialEditorValuesHash] = useState<string | null>(null);
  const [isEditorDirty, setIsEditorDirty] = useState(false);
  // CR-specific tracking: only price/count changes for CR_WAITFORHOTELAPPROVAL phase
  const [crInitialLineItemsHash, setCrInitialLineItemsHash] = useState<string | null>(null);
  const [crCurrentLineItemsHash, setCrCurrentLineItemsHash] = useState<string | null>(null);
  const [isCrPricesDirty, setIsCrPricesDirty] = useState(false);

  const [submitRunning, setSubmitRunning] = useState(false);
  const [forceReloadBackendChanges, setForceReloadBackendChanges] = useState(false);

  const [agbDialogOpen, setAgbDialogOpen] = useState(false);
  const [pendingApproval, setPendingApproval] = useState<(() => void) | null>(null);
  type GuestSuccessType = 'approved' | 'draft' | 'declined';
  const [guestSuccessDialog, setGuestSuccessDialog] = useState<GuestSuccessType | null>(null);
  const [customerCommentDialogOpen, setCustomerCommentDialogOpen] = useState(false);

  const isListerBooking = !!props.version.inputLister;
  const hotelMessage = (props.version.hotelMessage || '').trim();
  const requestFiles = props.version.requestFiles ?? [];
  // An offer or reservation PDF can be imported into the booking as a new version (with review first).
  const { data: pdfImportFeature } = useQuery<{ pdfOfferImportEnabled: boolean }>(PDF_IMPORT_ENABLED_QUERY);
  const canImportPdf = !!pdfImportFeature?.pdfOfferImportEnabled && !PDF_IMPORT_TARGET_CLOSED_STATUSES.includes(offerStatus);
  const { data: cancellationRulesData } = useQuery(OFFERVERSION_VIEW_CANCELLATION_RULES_QUERY, {
    variables: { id: props.version.id },
  });

  // Reset editor baseline when version changes
  useEffect(() => {
    setInitialEditorValuesHash(null);
    setIsEditorDirty(false);
    setCrInitialLineItemsHash(null);
    setIsCrPricesDirty(false);
  }, [props.version.id]);

  // Epilo offers awaiting hotel approval: prompt hotel to acknowledge booker comment before editing.
  useEffect(() => {
    if (
      !props.guestMode &&
      offerSource === EOfferSource.LISTER &&
      versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL &&
      (hotelMessage || requestFiles.length > 0)
    ) {
      setCustomerCommentDialogOpen(true);
    } else {
      setCustomerCommentDialogOpen(false);
    }
  }, [props.guestMode, offerSource, versionStatus, hotelMessage, requestFiles.length, props.version.id]);

  const handleApproval = (approvalFunction: () => void) => {
    // AGB dialog (incl. optional save-to-base-price-list) is Epilo-only; SEM is unchanged.
    const isNormalOfferConfirmation =
      isListerBooking &&
      (versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL || versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL);

    if (isNormalOfferConfirmation) {
      setPendingApproval(() => approvalFunction);
      setAgbDialogOpen(true);
    } else {
      approvalFunction();
    }
  };

  const handleAgbDialogConfirm = (nextSavePrices: boolean) => {
    setAgbDialogOpen(false);
    savePricesRef.current = nextSavePrices;
    if (pendingApproval) {
      pendingApproval();
      setPendingApproval(null);
    }
  };

  const handleAgbDialogClose = () => {
    setAgbDialogOpen(false);
    setPendingApproval(null);
  };

  const finishGuestOrNavigate = (guestType: GuestSuccessType, snackbarKey: string) => {
    if (props.guestMode) {
      setGuestSuccessDialog(guestType);
      return;
    }
    dispatchMessage(dispatch, i18next.t(snackbarKey));
    navigate(`/offers/${offerSource.toLowerCase()}`);
  };

  const handleRejectSubmit = async (reason: { code: EOfferRejectionReason; customText?: string }) => {
    setSubmitRunning(true);
    try {
      await rejectOfferVersion({
        variables: {
          offerVersionId: props.version.id,
          rejectionReason: reason,
        },
        update: cache => {
          EVICT_OFFER_QUERIES(cache);
          EVICT_INTEGRATION_HOTELLOG_QUERIES(cache);
        },
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
      });
      finishGuestOrNavigate('declined', 'offer-status-rejected');
    } catch (err) {
      dispatchException(dispatch, err);
    } finally {
      setSubmitRunning(false);
    }
  };

  const canChangeStructure = offerSource === EOfferSource.WIDGET;
  const canChangeItems = offerSource === EOfferSource.WIDGET;
  const canAddItems =
    offerSource === EOfferSource.WIDGET ||
    (offerSource === EOfferSource.LISTER &&
      (versionType === EOfferVersionType.OFFER || versionType === EOfferVersionType.EDITING) &&
      (versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL ||
        (versionStatus === EOfferVersionStatus.WAITFORSEMAPPROVAL && user.isSeminargo)));
  const canDeleteItems =
    offerSource === EOfferSource.WIDGET ||
    (offerSource === EOfferSource.LISTER &&
      (versionType === EOfferVersionType.OFFER || versionType === EOfferVersionType.EDITING) &&
      (versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
        versionStatus === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL ||
        (versionStatus === EOfferVersionStatus.WAITFORSEMAPPROVAL && user.isSeminargo)));
  const canChangePrices =
    offerSource === EOfferSource.WIDGET ||
    hasNoPrices ||
    versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL ||
    versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
    versionStatus === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL ||
    (versionStatus === EOfferVersionStatus.WAITFORSEMAPPROVAL && user.isSeminargo);

  const isChangeRequest = props.data.process === EOfferProcess.PREMIUM && props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL;
  const highlightChangeRequestItems = props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL;

  const _approveButtonLabel = () => {
    // SEM with APPROVED offer: always show "Geänderte Reservierungsbestätigung speichern und senden"
    // This applies to both RESERVATION_CONFIRMATION and EDITING (after saving draft)
    if (
      offerSource === EOfferSource.WIDGET &&
      offerStatus === EOfferStatus.APPROVED &&
      (versionType === EOfferVersionType.RESERVATION_CONFIRMATION || versionType === EOfferVersionType.EDITING)
    ) {
      return i18next.t('offer-process-approve-approved-change');
    }
    if (editing && isEditorDirty) {
      // Angebotsänderungen vorhanden
      if (
        props.version.status === EOfferVersionStatus.WAITFORCLIENTAPPROVAL ||
        props.version.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL
      ) {
        return i18next.t('offer-process-approve-as-client-change');
      }
      // CR-specific: only show asterisk if prices/counts actually changed
      if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL && !isCrPricesDirty) {
        return i18next.t('offer-process-approve-cr-no-changes');
      }
      // CR-specific: use offerStatus to differentiate reservation changes vs inquiry changes
      if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) {
        return offerStatus === EOfferStatus.APPROVED
          ? i18next.t('offer-process-approve-reservation-change')
          : i18next.t('offer-process-approve-change');
      }
      return i18next.t('offer-process-approve-change'); // "Ergänztes Angebot bestätigen"
    }
    // Keine Änderungen
    if (props.version.status === EOfferVersionStatus.WAITFORCLIENTAPPROVAL || props.version.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL) {
      return i18next.t('offer-process-approve-as-client');
    }
    if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) {
      const isReservationChange = offerStatus === EOfferStatus.APPROVED;
      const label = isReservationChange ? i18next.t('offer-process-approve-reservation-change') : i18next.t('offer-process-approve-offer');
      return label;
    }
    if (props.version.status === EOfferVersionStatus.WAITFORHOTELRESERVATION) {
      return i18next.t('offer-process-approve-reservation'); // "Reservierung bestätigen"
    }
    return i18next.t('offer-process-approve-offer'); // "Angebot bestätigen"
  };
  const _approveQuestionTitle = () => {
    if (props.version.status === EOfferVersionStatus.APPROVED) return i18next.t('offerconfirm-approve-approved-title');
    if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) {
      return offerStatus === EOfferStatus.APPROVED
        ? i18next.t('offerconfirm-approve-offer-change-title')
        : i18next.t('offerconfirm-approve-offer-title');
    }
    if (props.version.status === EOfferVersionStatus.WAITFORHOTELRESERVATION) return i18next.t('offerconfirm-approve-reservation-title');
    return i18next.t('offerconfirm-approve-offer-title');
  };

  const _approveQuestion = () => {
    if (editing) {
      if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) return i18next.t('offerconfirm-approve-cr-question-change');
      if (props.version.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL) return i18next.t('offerconfirm-approve-cr-question-change');
      if (props.version.status === EOfferVersionStatus.WAITFORCLIENTAPPROVAL) return i18next.t('offerconfirm-approve-as-client-question-change');
      if (props.version.status === EOfferVersionStatus.APPROVED) return i18next.t('offerconfirm-approve-approved-question-change');
      return i18next.t('offerconfirm-approve-question-change');
    } else {
      if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) return i18next.t('offerconfirm-approve-cr-question');
      if (props.version.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL) return i18next.t('offerconfirm-approve-cr-question');
      if (props.version.status === EOfferVersionStatus.WAITFORCLIENTAPPROVAL) return i18next.t('offerconfirm-approve-as-client-question');
      if (props.version.status === EOfferVersionStatus.WAITFORHOTELRESERVATION) return i18next.t('offerconfirm-approve-reservation-question');
      return i18next.t('offerconfirm-approve-offer-question');
    }
  };

  const _rejectButtonLabel = () => {
    if (props.version.status === EOfferVersionStatus.WAITFORCLIENTAPPROVAL) {
      return i18next.t('offer-process-withdraw-approval');
    }
    if (props.version.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL) {
      return i18next.t('offer-process-reject-cr');
    }
    if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) {
      return offerStatus === EOfferStatus.APPROVED ? i18next.t('offer-process-reject-cr-reservation') : i18next.t('offer-process-reject-cr');
    }
    if (props.version.status === EOfferVersionStatus.WAITFORHOTELRESERVATION) {
      return i18next.t('offer-process-reject-reservation');
    }
    return i18next.t('offer-process-reject-offer');
  };

  const _rejectQuestionTitle = () => {
    if (props.version.status === EOfferVersionStatus.WAITFORCLIENTAPPROVAL) {
      return i18next.t('offerconfirm-withdraw-title');
    }
    if (props.version.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL) {
      return i18next.t('offerconfirm-rejectcr-title');
    }
    if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) {
      return offerStatus === EOfferStatus.APPROVED ? i18next.t('offerconfirm-rejectcr-reservation-title') : i18next.t('offerconfirm-rejectcr-title');
    }
    return i18next.t('offerconfirm-reject-title');
  };

  const _rejectQuestion = () => {
    if (props.version.status === EOfferVersionStatus.WAITFORCLIENTAPPROVAL) {
      return i18next.t('offerconfirm-withdraw-text');
    }
    if (props.version.status === EOfferVersionStatus.CR_WAITFORCLIENTAPPROVAL) {
      return i18next.t('offerconfirm-rejectcr-text');
    }
    if (props.version.status === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL) {
      return offerStatus === EOfferStatus.APPROVED ? i18next.t('offerconfirm-rejectcr-reservation-text') : i18next.t('offerconfirm-rejectcr-text');
    }
    return i18next.t('offerconfirm-reject-text');
  };

  const editButtonLabel = isChangeRequest
    ? i18next.t('offer-approvecr-edit')
    : offerStatus === EOfferStatus.APPROVED
      ? i18next.t('offer-confirmation-process-edit')
      : i18next.t('offer-process-edit');

  const requestEditorSubmit = (processFlag: boolean) => {
    lastEditorSubmitWasApproveRef.current = processFlag;
    setSubmitProcess(processFlag);
    setSubmitTrigger(prev => prev + 1);
  };

  const handleSaveDraft = () => {
    if (!editing || !canEditorSave || !isEditorValid || submitRunning) return;
    setSubmitAndSendEmail(false);
    requestEditorSubmit(false);
  };

  const handleSaveAndSend = () => {
    if (!editing || !canEditorSave || !isEditorValid || submitRunning) return;

    // For WAITFORHOTELAPPROVAL / CR_WAITFORHOTELAPPROVAL / WAITFORSEMAPPROVAL: use approve flow (status transition triggers email)
    if (
      versionStatus === EOfferVersionStatus.WAITFORHOTELAPPROVAL ||
      versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL ||
      versionStatus === EOfferVersionStatus.WAITFORSEMAPPROVAL
    ) {
      setSubmitAndSendEmail(false);
      requestEditorSubmit(true);
      return;
    }

    // For WAITFORCLIENTAPPROVAL / CR_WAITFORCLIENTAPPROVAL: save as draft, then send email
    setSubmitAndSendEmail(true);
    requestEditorSubmit(false);
  };

  const handleResetChanges = async () => {
    setSubmitRunning(true);
    try {
      await resetOfferVersion({
        variables: {
          id: props.version.id,
        },
        update: cache => {
          EVICT_OFFER_QUERIES(cache);
          EVICT_INTEGRATION_HOTELLOG_QUERIES(cache);
        },
        awaitRefetchQueries: true,
        refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
      });
      dispatchMessage(dispatch, i18next.t('offer-process-reset-success'));
      navigate(`/offers/${offerSource.toLowerCase()}`);
    } catch (err) {
      dispatchException(dispatch, err);
    } finally {
      setSubmitRunning(false);
    }
  };

  const setShowOfferVersionId = (versionId: number) => {
    navigate(`/offers/${props.data.id}/${versionId}#editor`);
  };

  const [canSeeCustomerData] = useState(props.data.source === EOfferSource.WIDGET || user.isSeminargo || props.data.status === EOfferStatus.APPROVED);
  const [canEditCustomerData] = useState(props.data.source === EOfferSource.WIDGET || user.isSeminargo);

  const _editorDoSubmit = async (
    values: OfferVersionFormType,
    switchServiceTypeSku?: string | null,
    approve = true,
  ): Promise<WLOfferVersionListOutput> => {
    setSubmitRunning(true);
    try {
      const res = await upsertMutateFunction({
        variables: {
          id: props.version.id,
          data: {
            additionalInfo1: values.additionalInfo1,
            additionalInfo2: values.additionalInfo2,
            additionalInfo3: values.additionalInfo3,
            personalMessage: values.personalMessage,
            hotelId: props.data.hotel.id,
            priceListId: props.version.priceList.id,
            version: values.version,
            title: values.title || null,
            filename: values.filename || null,
            offerDate: values.offerDate,
            startDate: values.startDate,
            headerTemplate: values.pageOptions.headerTemplate || null,
            footerTemplate: values.pageOptions.footerTemplate || null,
            marginTop: values.pageOptions.marginTop,
            marginBottom: values.pageOptions.marginBottom,
            marginLeft: values.pageOptions.marginLeft,
            marginRight: values.pageOptions.marginRight,
            includeEmptyLineItems: values.includeEmptyLineItems,
            cssStyles: values.pageOptions.cssStyles,
            contentBlocks: values.contentBlocks.map(cb => ({
              template: cb.template,
              sequence: cb.sequence,
              type: cb.type as EOfferVersionContentBlockType,
              formFields: (cb.formFields || []).map(ff => ({
                sequence: ff.sequence,
                type: ff.type as EOfferVersionContentBlockFormFieldType,
                label: ff.label,
                name: ff.name,
                value: ff.value,
                required: ff.required,
              })),
              lineItems: (cb.lineItems || []).map(li => ({
                day: li.day,
                sku: li.sku || null,
                header: li.header,
                details: li.details || '',
                count: li.count,
                sequence: li.sequence,
                priceItem: li.priceItem,
                priceNet: li.priceNet,
                priceGross: li.priceGross,
                components: li.components.map(t => ({
                  price: t.price,
                  taxTypeId: t.taxTypeId,
                })),
                taxes: li.taxes.map(t => ({
                  price: t.price,
                  taxRateId: t.taxRateId,
                })),
                source: li.source,
                guaranteedRoomAreaSqm: li.guaranteedRoomAreaSqm ?? null,
                confirmAirConditioning: li.confirmAirConditioning ?? null,
                confirmDaylight: li.confirmDaylight ?? null,
                requirementsAcknowledged: !!li.requirementsAcknowledged,
                offeredSeatCount: li.offeredSeatCount ?? null,
                offeredFrom: li.offeredFrom ?? null,
                offeredTo: li.offeredTo ?? null,
                offeredSeating: (li.offeredSeating || null) as EOfferRoomSeating | null,
                offeredBeamerCount: li.offeredBeamerCount ?? null,
                offeredFlipchartCount: li.offeredFlipchartCount ?? null,
                offeredPinboardCount: li.offeredPinboardCount ?? null,
              })),
            })),
            totalPriceGross: values.totalPriceGross,
            totalPriceNet: values.totalPriceNet,
            taxes: values.totalTaxes.map(t => ({
              price: t.price,
              taxRateId: t.taxRateId,
            })),
          },
          client:
            canEditCustomerData && values.client
              ? {
                  email: values.client.email,
                  phone: values.client.phone,
                  firstname: values.client.firstname,
                  lastname: values.client.lastname,
                  company: values.client.company,
                  country: values.client.country,
                  address: values.client.address,
                  city: values.client.city,
                  zip: values.client.zip,
                  billingCompany: values.client.billingCompany,
                  billingFirstname: values.client.billingFirstname,
                  billingLastname: values.client.billingLastname,
                  billingAddress: values.client.billingAddress,
                  billingCity: values.client.billingCity,
                  billingZip: values.client.billingZip,
                  billingCountry: values.client.billingCountry,
                }
              : undefined,
          switchServiceTypeSku: switchServiceTypeSku,
          savePrices: savePricesRef.current,
          approve,
          crHasPriceChanges:
            versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL && offerSource === EOfferSource.LISTER ? isCrPricesDirty : null,
        },
        update: cache => EVICT_OFFER_QUERIES(cache),
        // Approve navigates away immediately; don't block on refetch (avoids editor race/crash).
        // Guest deeplink stays on page after approve, so await refetch there.
        awaitRefetchQueries: !approve || props.guestMode,
        refetchQueries: result => REFETCH_OFFER_QUERIES(props.data.id, result.data?.upsertOfferVersion?.id ?? props.version.id),
      });

      const savedVersion = res.data!.upsertOfferVersion as WLOfferVersionListOutput;

      // If submitAndSendEmail flag is set, send email after saving
      if (submitAndSendEmail && !approve) {
        try {
          await resendOfferEmailClient({
            variables: {
              offerVersionId: savedVersion.id,
            },
          });
          dispatchMessage(dispatch, i18next.t('offer-save-and-send-success'));

          // Refetch to update email history
          await Promise.all(
            REFETCH_OFFER_QUERIES(props.data.id, savedVersion.id).map(query =>
              client.query({
                ...query,
                fetchPolicy: 'network-only',
              }),
            ),
          );
        } catch (emailErr) {
          dispatchException(dispatch, emailErr);
        } finally {
          setSubmitAndSendEmail(false);
        }
      } else if (!props.guestMode) {
        dispatchMessage(dispatch, i18next.t('offerversion-saved'));
      }

      return savedVersion;
    } finally {
      setSubmitProcess(true);
      setSubmitRunning(false);
    }
  };

  const eventDays = _.orderBy(
    (props.event?.eventDays || []).filter(d => d.day >= 0),
    d => d.day,
    'asc',
  );
  const attendanceDays = _.uniq((props.event?.attendances || []).reduce<number[]>((agg, a) => [...agg, ...a.days.map(d => d.day)], [])).sort();

  const content = (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t(`offers-list-page-title-${offerSource}`), props.data])}</title>
      </Helmet>
      {!props.guestMode && (
        <SubscribeToOfferChange
          offerId={props.data.id}
          onChanged={() => {
            if (submitRunning || submitTrigger > 0) return;
            setForceReloadBackendChanges(true);
          }}
        />
      )}
      <ScrollToTopButton />
      {forceReloadBackendChanges && (
        <InformationDialog
          open={true}
          title={i18next.t('offer-reload-title')}
          onConfirm={() => {
            if (props.guestMode) {
              window.location.reload();
            } else {
              navigate(`/offers/${offerSource.toLowerCase()}`);
            }
          }}
        >
          <Typography>{i18next.t('offer-reload-text')}</Typography>
        </InformationDialog>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              {offerSource === EOfferSource.LISTER ? (
                <>
                  <Typography variant="h6">
                    <StableInlineText>{`${props.version.extRefCode || props.data.refCode} - ${props.version.title}`}</StableInlineText>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <StableInlineText>
                      {i18next.t(
                        props.version.status === 'CR_WAITFORHOTELAPPROVAL' && props.data.status === 'APPROVED'
                          ? 'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION'
                          : `enums-EOfferVersionStatus-${props.version.status}`,
                      )}
                    </StableInlineText>
                  </Typography>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">
                    <StableInlineText>{`${props.data.refCode} - ${i18next.t(
                      props.version.status === 'CR_WAITFORHOTELAPPROVAL' && props.data.status === 'APPROVED'
                        ? 'enums-EOfferVersionStatus-CR_WAITFORHOTELAPPROVAL-RESERVATION'
                        : `enums-EOfferVersionStatus-${props.version.status}`,
                    )}`}</StableInlineText>
                  </Typography>
                  {!props.guestMode && offerSource === EOfferSource.WIDGET && (
                    <OfferDuplicateButton
                      offerVersionId={props.version.id}
                      refCode={props.data.refCode}
                      companyName={props.version.client?.company ?? props.version.client?.billingCompany}
                      sourceStartDate={props.version.input?.startDate}
                    />
                  )}
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={7} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1,
                  maxWidth: '100%',
                  overflowX: { xs: 'visible', md: 'auto' },
                  overflowY: 'hidden',
                  pb: { md: 0.25 },
                  '& .MuiButton-root': { flexShrink: 0 },
                }}
              >
                {!editing && canDirectApprove && (
                  <Button
                    disabled={hasNoPrices || !isEditorValid}
                    variant="contained"
                    startIcon={<ThumbUpIcon />}
                    onClick={() =>
                      handleApproval(async () => {
                        setSubmitRunning(true);
                        try {
                          await approveOfferVersion({
                            variables: {
                              offerVersionId: props.version.id,
                            },
                            update: cache => {
                              EVICT_OFFER_QUERIES(cache);
                              EVICT_INTEGRATION_HOTELLOG_QUERIES(cache);
                            },
                            awaitRefetchQueries: true,
                            refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
                          });
                          finishGuestOrNavigate('approved', 'offer-status-approved');
                        } catch (err) {
                          dispatchException(dispatch, err);
                        } finally {
                          setSubmitRunning(false);
                        }
                      })
                    }
                  >
                    <StableInlineText>{_approveButtonLabel()}</StableInlineText>
                  </Button>
                )}
                {editing &&
                  // For SEM with APPROVED offer: always show button when editing (RESERVATION_CONFIRMATION or EDITING after draft save)
                  (canDirectApprove ||
                    (offerSource === EOfferSource.WIDGET &&
                      offerStatus === EOfferStatus.APPROVED &&
                      (versionType === EOfferVersionType.RESERVATION_CONFIRMATION || versionType === EOfferVersionType.EDITING))) && (
                    <Button
                      disabled={
                        // For SEM with APPROVED offer: require changes to enable
                        offerSource === EOfferSource.WIDGET &&
                        offerStatus === EOfferStatus.APPROVED &&
                        (versionType === EOfferVersionType.RESERVATION_CONFIRMATION || versionType === EOfferVersionType.EDITING)
                          ? !isEditorDirty || !canEditorSave || !isEditorValid
                          : isEditorDirty
                            ? !canEditorSave || !isEditorValid
                            : hasNoPrices || !canDirectApprove || !isEditorValid
                      }
                      variant="contained"
                      startIcon={<ThumbUpIcon />}
                      onClick={() =>
                        handleApproval(async () => {
                          // CR phase on a confirmed booking: auto-approve when no prices changed (RESERVATION_CONFIRMATION baseline)
                          if (
                            versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL &&
                            offerSource === EOfferSource.LISTER &&
                            offerStatus === EOfferStatus.APPROVED
                          ) {
                            // No price/count changes: auto-approve (backend will transition to APPROVED)
                            if (!isCrPricesDirty) {
                              setSubmitAndSendEmail(false);
                              requestEditorSubmit(false); // Save only, backend auto-approves
                              return;
                            }
                            // Price/count changes: proceed to CR_WAITFORCLIENTAPPROVAL
                            requestEditorSubmit(true);
                            return;
                          }
                          // Always persist current editor state before approving so added line items are not lost.
                          requestEditorSubmit(true);
                        })
                      }
                    >
                      <StableInlineText>{_approveButtonLabel()}</StableInlineText>
                    </Button>
                  )}

                {canReject && (
                  <Button
                    disabled={isEditorDirty || submitRunning}
                    variant="contained"
                    color="secondary"
                    startIcon={<ThumbDownIcon />}
                    onClick={() => setRejectDialogOpen(true)}
                  >
                    <StableInlineText>{_rejectButtonLabel()}</StableInlineText>
                  </Button>
                )}
                {canConfirmCancellation && (
                  <ConfirmationButton
                    variant="contained"
                    startIcon={<CheckIcon />}
                    disabled={submitRunning}
                    confirmationTitle={i18next.t('offer-confirm-cancellation-question-title')}
                    confirmationQuestion={i18next.t('offer-confirm-cancellation-question')}
                    onConfirm={async () => {
                      setSubmitRunning(true);
                      try {
                        await confirmCancellationHotel({
                          variables: {
                            offerVersionId: props.version.id,
                          },
                          update: cache => {
                            EVICT_OFFER_QUERIES(cache);
                          },
                          awaitRefetchQueries: true,
                          refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
                        });
                        dispatchMessage(dispatch, i18next.t('offer-confirm-cancellation-success'));
                        navigate(`/offers/${offerSource.toLowerCase()}`);
                      } catch (err) {
                        dispatchException(dispatch, err);
                      } finally {
                        setSubmitRunning(false);
                      }
                    }}
                  >
                    <StableInlineText>{i18next.t('offer-confirm-cancellation-button')}</StableInlineText>
                  </ConfirmationButton>
                )}
                {canChange && !editing && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setEditing(true);
                      setSelectedTab(1);
                    }}
                  >
                    <StableInlineText>{editButtonLabel}</StableInlineText>
                  </Button>
                )}
                {canCancel && offerSource === EOfferSource.WIDGET && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<DoNotDisturbIcon />}
                    onClick={() => {
                      navigate(`/offers/cancelsem/${props.data.id}/${props.version.id}`);
                    }}
                  >
                    <StableInlineText>{i18next.t('offer-cancel-button')}</StableInlineText>
                  </Button>
                )}
                {canImportPdf && (
                  <Button
                    disabled={isEditorDirty}
                    variant="contained"
                    color="secondary"
                    startIcon={<UploadFileIcon />}
                    onClick={() => navigate(`/offers/pdf-import?offerId=${props.data.id}`)}
                  >
                    <StableInlineText>{i18next.t('pdf-import-offer-button')}</StableInlineText>
                  </Button>
                )}
                {/* Standalone Download Button (only when !canSaveChangesEPILO) */}
                {!canSaveChangesEPILO && canDownload && (
                  <Button
                    disabled={isEditorDirty}
                    variant="contained"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      window.open(getFullBackendUrl(`/backend/pdf/downloadOffer/${props.version.id}`), '_blank');
                    }}
                  >
                    <StableInlineText>
                      {useCancellationConfirmationDownloadLabel
                        ? i18next.t('offer-cancellation-confirmation-process-download')
                        : useReservationConfirmationDownloadLabel
                          ? i18next.t('offer-confirmation-process-download')
                          : i18next.t('offer-process-download')}
                    </StableInlineText>
                  </Button>
                )}

                {/* Action Dropdown Menu (only when canSaveChangesEPILO) */}
                {canSaveChangesEPILO && (
                  <OfferActionMenu
                    canSave={canSaveChangesEPILO && editing && isEditorDirty}
                    canDownload={canDownload}
                    onSave={handleSaveDraft}
                    onDownload={() => {
                      window.open(getFullBackendUrl(`/backend/pdf/downloadOffer/${props.version.id}`), '_blank');
                    }}
                    disabled={submitRunning || resendOfferEmailLoading}
                    useReservationConfirmationDownloadLabel={useReservationConfirmationDownloadLabel}
                    useCancellationConfirmationDownloadLabel={useCancellationConfirmationDownloadLabel}
                    isEditorDirty={isEditorDirty}
                  />
                )}
                {canSaveChanges && (
                  <>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={!editing || !canEditorSave || !isEditorValid || !isEditorDirty || submitRunning || resendOfferEmailLoading}
                      startIcon={<SaveAsIcon />}
                      onClick={handleSaveDraft}
                    >
                      <StableInlineText>
                        {versionType === EOfferVersionType.RESERVATION_CONFIRMATION ||
                        (offerStatus === EOfferStatus.APPROVED && versionType === EOfferVersionType.EDITING)
                          ? i18next.t('offer-confirmation-save-changes')
                          : i18next.t('offer-save-changes')}
                      </StableInlineText>
                    </Button>
                    {!isNewInquiry &&
                      // In SEM (WIDGET) once a booking is APPROVED/ACTIVE, do not allow "save and send offer" again.
                      // That action triggers resendOfferEmailClient which can change version/offer state and make the booking disappear from the list.
                      !(offerSource === EOfferSource.WIDGET && offerStatus === EOfferStatus.APPROVED) &&
                      !(versionType === EOfferVersionType.RESERVATION_CONFIRMATION && versionStatus === EOfferVersionStatus.APPROVED) && (
                        <Button
                          variant="contained"
                          disabled={!editing || !canEditorSave || !isEditorValid || !isEditorDirty || submitRunning || resendOfferEmailLoading}
                          startIcon={<ThumbUpIcon />}
                          onClick={handleSaveAndSend}
                        >
                          <StableInlineText>{i18next.t('offer-save-and-send')}</StableInlineText>
                        </Button>
                      )}
                  </>
                )}
                {canResetChanges && (
                  <ConfirmationButton
                    variant="contained"
                    color="secondary"
                    startIcon={<DeleteIcon />}
                    disabled={submitRunning}
                    confirmationTitle={i18next.t('offer-process-reset-title')}
                    confirmationQuestion={i18next.t('offer-process-reset-text')}
                    onConfirm={handleResetChanges}
                  >
                    <StableInlineText>{i18next.t('offer-process-reset')}</StableInlineText>
                  </ConfirmationButton>
                )}
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ContactPersonTabsWrapper responsibleUser={props.data.responsibleUser}>
            <CustomTabsControlled
              selected={selectedTab}
              handleChange={selected => setSelectedTab(selected)}
              headers={[
                i18next.t('offer-input-tab'),
                editing
                  ? i18next.t('offer-editor-tab')
                  : isChangeRequest
                    ? i18next.t('offer-approvecr-tab')
                    : offerStatus === EOfferStatus.APPROVED
                      ? i18next.t('offer-confirmation-editor-tab')
                      : i18next.t('offer-editor-tab'),
                i18next.t('offer-approvecr-tab'),
                i18next.t('offer-integration-tab'),
                i18next.t('offer-event-tab'),
                i18next.t('offer-tasks-tab'),
                i18next.t('offer-history-tab'),
              ]}
              disabled={[false, false, false, false, false, false, false]}
              hidden={[
                false,
                false,
                true, //props.version.status !== EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL || props.version.type === EOfferVersionType.OFFER,
                props.guestMode || props.data.hotel.integrations.length === 0,
                props.guestMode || props.event === null,
                !(offerSource === EOfferSource.LISTER && (user.isSeminargo || user.isRoot)),
                false,
              ]}
              renderHidden={[false, true, false, false, false, false, false]}
              icons={[
                undefined,
                editing ? isEditorValid ? undefined : <ErrorIcon /> : undefined,
                <ErrorIcon />,
                undefined,
                undefined,
                undefined,
                undefined,
              ]}
              tabs={[
                <OfferInputView
                  data={props.data}
                  version={props.version}
                  periodStartDate={offerSource === EOfferSource.WIDGET ? offerPeriod?.startDate : null}
                  periodEndDate={offerSource === EOfferSource.WIDGET ? offerPeriod?.endDate : null}
                />,
                <>
                  {versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL && offerStatus === EOfferStatus.APPROVED && (
                    <>
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        <Alert severity="warning">
                          <Typography variant="body2">
                            <StableInlineText>{i18next.t('offer-cr-waitforhotelapproval-warning')}</StableInlineText>
                          </Typography>
                        </Alert>
                      </Grid>
                      {hasRecentAddonRemovals(props.data.history ?? []) && (
                        <Grid item xs={12} sx={{ mb: 2 }}>
                          <AddonChangesSummary history={props.data.history} startDate={props.version.startDate} hotelId={props.data.hotel.id} />
                        </Grid>
                      )}
                    </>
                  )}
                  <OfferVersion
                    versionId={props.version.id}
                    offerId={props.data.id}
                    offerSource={props.data.source}
                    disabled={!editing || !canChange}
                    canChangeCounts={editing && canChangePrices}
                    canChangeItems={editing && canChangeItems}
                    canChangePrices={editing && canChangePrices}
                    canChangeStructure={editing && canChangeStructure}
                    canAddItems={editing && canAddItems}
                    canDeleteItems={editing && canDeleteItems}
                    canChangeClientData={editing && canEditCustomerData}
                    isChangeRequest={isChangeRequest}
                    highlightChangeRequestItems={highlightChangeRequestItems}
                    diffToApproved={props.version.diffToApproved}
                    canSwitchServiceType={offerSource === EOfferSource.WIDGET}
                    canSeeClientData={canSeeCustomerData}
                    onChangeIsValid={isValid => setIsEditorValid(isValid)}
                    onChangeCanSaveState={canSave => setCanEditorSave(canSave)}
                    onOfferPeriodChange={handleOfferPeriodChange}
                    onChangeFormValues={async values => {
                      if (!editing) return;

                      const valuesHash = await sha256Hex(JSON.stringify(values));
                      if (initialEditorValuesHash !== null) {
                        setIsEditorDirty(valuesHash !== initialEditorValuesHash);
                      } else {
                        // First debounced snapshot becomes the baseline for dirty detection.
                        setInitialEditorValuesHash(valuesHash);
                        setIsEditorDirty(false);
                      }

                      // CR-specific: track only price/count changes for LISTER
                      if (versionStatus === EOfferVersionStatus.CR_WAITFORHOTELAPPROVAL && offerSource === EOfferSource.LISTER) {
                        const crLineItemsHash = await sha256Hex(extractCrLineItemsData(values));
                        setCrCurrentLineItemsHash(crLineItemsHash);
                        if (crInitialLineItemsHash !== null) {
                          setIsCrPricesDirty(crLineItemsHash !== crInitialLineItemsHash);
                        } else {
                          // Baseline not set yet
                          setCrInitialLineItemsHash(crLineItemsHash);
                          setIsCrPricesDirty(false);
                        }
                      }
                    }}
                    submitTrigger={submitTrigger}
                    submitProcess={submitProcess}
                    doSubmit={_editorDoSubmit}
                    onSubmitted={() => {
                      if (props.guestMode) {
                        setGuestSuccessDialog(lastEditorSubmitWasApproveRef.current ? 'approved' : 'draft');
                        return;
                      }
                      navigate(`/offers/${offerSource.toLowerCase()}`);
                    }}
                    guestMode={props.guestMode}
                  />
                </>,
                <Grid container>
                  {props.version.diffToApproved && (
                    <Grid item xs={6}>
                      <Grid container>
                        {props.version.diffToApproved!.startDateChanged && (
                          <Grid item xs={12}>
                            {i18next.t('offer-input-changed-startdate', {
                              fromStartDate: formatDate(props.version.diffToApproved!.startDateChanged.fromStartDate),
                              toStartDate: formatDate(props.version.diffToApproved!.startDateChanged.toStartDate),
                            })}
                          </Grid>
                        )}
                        {props.version.diffToApproved!.diff.map((dayDiff, index) => (
                          <Grid key={index} item xs={12} sx={{ padding: 1 }}>
                            <SimpleAccordion
                              autoOpen={true}
                              header={
                                <>
                                  {dayDiff.day === -1 && i18next.t('offer-input-header-preday')}
                                  {dayDiff.day >= 0 && (
                                    <>
                                      {i18next.t('offer-input-header-day')} {dayDiff.day + 1}
                                    </>
                                  )}
                                </>
                              }
                            >
                              <Grid container>
                                <Grid item xs={12}>
                                  <Grid container>
                                    <Grid item xs={12} sx={{ padding: 1 }}>
                                      <SimpleTable
                                        headers={[
                                          i18next.t('offer-input-line-sku'),
                                          i18next.t('offer-input-line-header'),
                                          i18next.t('offer-input-line-count1'),
                                          i18next.t('offer-input-line-count2'),
                                          i18next.t('offer-input-line-diff'),
                                        ]}
                                        rows={dayDiff.diffs
                                          .filter(d => d.hasCountDiff)
                                          .map((row, index) => [
                                            row.sku,
                                            row.header,
                                            row.count1,
                                            row.count2,
                                            row.count2! > row.count1! ? `+${row.count2! - row.count1!}` : `-${row.count1! - row.count2!}`,
                                          ])}
                                      />
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </SimpleAccordion>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}
                </Grid>,
                <Grid container>
                  <Grid item xs={12} sm={9}>
                    <Button
                      sx={{ marginRight: 2 }}
                      variant="contained"
                      color="secondary"
                      startIcon={viewInfoLoading ? <CircularProgress size={24} /> : <OpenInNewIcon />}
                      disabled={!canBookingOpenPMS(props.version) || viewInfoLoading}
                      onClick={async () => {
                        try {
                          const res = await viewInfoQuery({
                            variables: {
                              offerVersionId: props.version.id,
                            },
                          });
                          if (res.data?.integrationViewGroupBookingInfo && res.data?.integrationViewGroupBookingInfo.href) {
                            window.open(res.data?.integrationViewGroupBookingInfo.href, '_blank');
                          } else {
                            dispatchMessage(dispatch, i18next.t('offer-process-openbooking-nohref'));
                          }
                        } catch (err) {
                          dispatchException(dispatch, err);
                        }
                      }}
                    >
                      {i18next.t('offer-process-openbooking')}
                    </Button>
                    <Button
                      sx={{ marginRight: 2 }}
                      variant="contained"
                      color="secondary"
                      startIcon={syncMutateLoading ? <CircularProgress size={24} /> : <SyncIcon />}
                      disabled={!canBookingSendPMS(props.version) || syncMutateLoading}
                      onClick={async () => {
                        try {
                          const res = await syncMutateFunction({
                            variables: {
                              offerVersionId: props.version.id,
                            },
                            update: cache => EVICT_INTEGRATION_HOTELLOG_QUERIES(cache),
                            refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
                          });
                          if (res.data?.integrationSyncBooking) {
                            if (res.data?.integrationSyncBooking.status === 'SUCCESS') {
                              dispatchMessage(dispatch, i18next.t('offer-process-syncbooking-success'));
                            }
                            setLogUpdateCounter(logUpdateCounter + 1);
                          }
                        } catch (err) {
                          dispatchException(dispatch, err);
                        }
                      }}
                    >
                      {i18next.t('offer-process-syncbooking')}
                    </Button>
                    <Button
                      sx={{ marginRight: 2 }}
                      variant="contained"
                      color="secondary"
                      startIcon={confirmMutateLoading ? <CircularProgress size={24} /> : <CheckIcon />}
                      disabled={!canBookingConfirmPMS(props.version) || confirmMutateLoading}
                      onClick={async () => {
                        try {
                          const res = await confirmMutateFunction({
                            variables: {
                              offerVersionId: props.version.id,
                            },
                            update: cache => EVICT_INTEGRATION_HOTELLOG_QUERIES(cache),
                            refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
                          });
                          if (res.data?.integrationConfirmBooking) {
                            if (res.data?.integrationConfirmBooking.status === 'SUCCESS') {
                              dispatchMessage(dispatch, i18next.t('offer-process-confirmbooking-success'));
                            }
                            setLogUpdateCounter(logUpdateCounter + 1);
                          }
                        } catch (err) {
                          dispatchException(dispatch, err);
                        }
                      }}
                    >
                      {i18next.t('offer-process-confirmbooking')}
                    </Button>
                    <Button
                      sx={{ marginRight: 2 }}
                      variant="contained"
                      color="secondary"
                      startIcon={deleteMutateLoading ? <CircularProgress size={24} /> : <DeleteIcon />}
                      disabled={!canBookingDeletePMS(props.version) || deleteMutateLoading}
                      onClick={async () => {
                        try {
                          const res = await deleteMutateFunction({
                            variables: {
                              offerVersionId: props.version.id,
                            },
                            update: cache => EVICT_INTEGRATION_HOTELLOG_QUERIES(cache),
                            refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
                          });
                          if (res.data?.integrationDeleteBooking) {
                            if (res.data?.integrationDeleteBooking.status === 'SUCCESS') {
                              dispatchMessage(dispatch, i18next.t('offer-process-deletebooking-success'));
                            }
                            setLogUpdateCounter(logUpdateCounter + 1);
                          }
                        } catch (err) {
                          dispatchException(dispatch, err);
                        }
                      }}
                    >
                      {i18next.t('offer-process-deletebooking')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={i18next.t('offer-process-extrefcode')}
                      value={props.version.extRefCode || ''}
                      disabled
                      InputProps={{
                        endAdornment: props.version.extRefData && (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setViewExtRefData(props.version.extRefData)}>
                              <CodeIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ padding: 1 }}></Grid>
                  <Grid item xs={12}>
                    <IntegrationLogsTable key={logUpdateCounter} variables={{ spaceId: props.data.space.id, offerId: props.data.id }} />
                  </Grid>
                  <InformationDialog
                    title={i18next.t('offer-process-extrefdata')}
                    open={!!viewExtRefData}
                    onConfirm={() => setViewExtRefData(null)}
                    maxWidth="lg"
                    fullWidth
                  >
                    <Box p={2}>
                      <Grid container>
                        <Grid item xs={12}>
                          <pre>{viewExtRefData && JSON.stringify(JSON.parse(viewExtRefData), null, 2)}</pre>
                        </Grid>
                      </Grid>
                    </Box>
                  </InformationDialog>
                </Grid>,
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <SimpleAccordion header={`${i18next.t('offer-event-mealtimes')}`} autoOpen>
                      <SimpleTable
                        fullWidth={false}
                        headers={[
                          i18next.t('offer-event-mealtimes-day'),
                          i18next.t('offer-event-mealtimes-ek'),
                          i18next.t('offer-event-mealtimes-kp1'),
                          i18next.t('offer-event-mealtimes-me'),
                          i18next.t('offer-event-mealtimes-kp2'),
                          i18next.t('offer-event-mealtimes-ae'),
                          i18next.t('offer-event-mealtimes-ez'),
                          i18next.t('offer-event-mealtimes-dz'),
                        ]}
                        rows={eventDays.map((day, dindex) => [
                          <StableInlineText>{formatOfferDay(day.day)}</StableInlineText>,
                          <InlineIconText
                            icon={<PeopleIcon />}
                          >{`${day.morningTime} - ${(props.event?.attendances || []).reduce((sum, a) => sum + (a.days.findIndex(d => d.day === day.day && d.morning) >= 0 ? 1 : 0), 0)}`}</InlineIconText>,
                          <InlineIconText
                            icon={<PeopleIcon />}
                          >{`${day.forenoonTime} - ${(props.event?.attendances || []).reduce((sum, a) => sum + (a.days.findIndex(d => d.day === day.day && d.forenoon) >= 0 ? 1 : 0), 0)}`}</InlineIconText>,
                          <InlineIconText
                            icon={<PeopleIcon />}
                          >{`${day.lunchTime} - ${(props.event?.attendances || []).reduce((sum, a) => sum + (a.days.findIndex(d => d.day === day.day && d.lunch) >= 0 ? 1 : 0), 0)}`}</InlineIconText>,
                          <InlineIconText
                            icon={<PeopleIcon />}
                          >{`${day.afternoonTime} - ${(props.event?.attendances || []).reduce((sum, a) => sum + (a.days.findIndex(d => d.day === day.day && d.afternoon) >= 0 ? 1 : 0), 0)}`}</InlineIconText>,
                          <InlineIconText
                            icon={<PeopleIcon />}
                          >{`${day.dinnerTime} - ${(props.event?.attendances || []).reduce((sum, a) => sum + (a.days.findIndex(d => d.day === day.day && d.dinner) >= 0 ? 1 : 0), 0)}`}</InlineIconText>,
                          <InlineIconText
                            icon={<PeopleIcon />}
                          >{`${(props.event?.attendances || []).reduce((sum, a) => sum + (a.days.findIndex(d => d.day === day.day && d.room === EAMRoomType.ROOM_SINGLE) >= 0 ? 1 : 0), 0)}`}</InlineIconText>,
                          <InlineIconText
                            icon={<PeopleIcon />}
                          >{`${(props.event?.attendances || []).reduce((sum, a) => sum + (a.days.findIndex(d => d.day === day.day && d.room === EAMRoomType.ROOM_DOUBLE) >= 0 ? 1 : 0), 0)}`}</InlineIconText>,
                        ])}
                      />
                    </SimpleAccordion>
                  </Grid>
                  <Grid item xs={12}>
                    <SimpleAccordion header={`${i18next.t('offer-event-rooms')}`} autoOpen>
                      {eventDays.map((eventDay, edindex) => (
                        <SimpleTable
                          fullWidth={false}
                          key={edindex}
                          headers={[
                            formatOfferDay(eventDay.day),
                            ...eventDay.rooms.map((r, rindex) => i18next.t('offer-event-rooms-number', { roomNumber: rindex + 1 })),
                          ]}
                          rows={[
                            [
                              i18next.t('offer-event-rooms-duration'),
                              ...eventDay.rooms.map((r, rindex) => <StableInlineText key={rindex}>{`${r.fromTime} - ${r.toTime}`}</StableInlineText>),
                            ],
                            [
                              i18next.t('offer-event-rooms-seating'),
                              ...eventDay.rooms.map((r, rindex) => (
                                <StableInlineText key={rindex}>{i18next.t(`enums-EOfferRoomSeating-${r.seating}`)}</StableInlineText>
                              )),
                            ],
                            [
                              i18next.t('offer-event-rooms-guests'),
                              ...eventDay.rooms.map((r, rindex) => (
                                <InlineIconText key={rindex} icon={<PeopleIcon />}>{`${r.guestCount}`}</InlineIconText>
                              )),
                            ],
                            [
                              i18next.t('offer-event-rooms-label'),
                              ...eventDay.rooms.map((r, rindex) => <StableInlineText key={rindex}>{r.label}</StableInlineText>),
                            ],
                            [
                              i18next.t('offer-event-rooms-facilities'),
                              ...eventDay.rooms.map((r, rindex) => <StableInlineText key={rindex}>{r.commentFacilities}</StableInlineText>),
                            ],
                          ]}
                        />
                      ))}
                    </SimpleAccordion>
                  </Grid>
                  <Grid item xs={12}>
                    <SimpleAccordion header={`${i18next.t('offer-event-attendances')}`} autoOpen>
                      {attendanceDays.map((aDay, adindex) => (
                        <SimpleTable
                          fullWidth={false}
                          key={adindex}
                          headers={[
                            formatOfferDay(aDay),
                            i18next.t('offer-event-attendances-ek'),
                            i18next.t('offer-event-attendances-kp1'),
                            i18next.t('offer-event-attendances-me'),
                            i18next.t('offer-event-attendances-kp2'),
                            i18next.t('offer-event-attendances-ae'),
                            i18next.t('offer-event-attendances-ez'),
                            i18next.t('offer-event-attendances-dz'),
                            i18next.t('offer-event-attendances-coach'),
                            i18next.t('offer-event-attendances-dpservice'),
                            i18next.t('offer-event-attendances-dproom'),
                            i18next.t('offer-event-attendances-dpextra'),
                            i18next.t('offer-event-attendances-dpparking'),
                          ]}
                          rows={(props.event?.attendances || [])
                            .map((a, aindex) => {
                              const ad = a.days.find(ad => ad.day === aDay);
                              if (ad) {
                                return [
                                  <StableInlineText>{`${a.profile.lastname} ${a.profile.firstname}`}</StableInlineText>,
                                  <>{ad.morning && <CheckIcon />}</>,
                                  <>{ad.forenoon && <CheckIcon />}</>,
                                  <>{ad.lunch && <CheckIcon />}</>,
                                  <>{ad.afternoon && <CheckIcon />}</>,
                                  <>{ad.dinner && <CheckIcon />}</>,
                                  <>{ad.room === EAMRoomType.ROOM_SINGLE && <CheckIcon />}</>,
                                  <>{ad.room === EAMRoomType.ROOM_DOUBLE && <CheckIcon />}</>,
                                  <>{ad.coach && <CheckIcon />}</>,
                                  <>
                                    {(!_.isNull(ad.serviceDirectPay)
                                      ? ad.serviceDirectPay
                                      : !_.isNull(a.serviceDirectPay)
                                        ? a.serviceDirectPay
                                        : !_.isNull(props.event?.serviceDirectPay)
                                          ? props.event?.serviceDirectPay!
                                          : false) && <CheckIcon />}
                                  </>,
                                  <>
                                    {(!_.isNull(ad.roomDirectPay)
                                      ? ad.roomDirectPay
                                      : !_.isNull(a.roomDirectPay)
                                        ? a.roomDirectPay
                                        : !_.isNull(props.event?.roomDirectPay)
                                          ? props.event?.roomDirectPay!
                                          : false) && <CheckIcon />}
                                  </>,
                                  <>
                                    {(!_.isNull(ad.extraDirectPay)
                                      ? ad.extraDirectPay
                                      : !_.isNull(a.extraDirectPay)
                                        ? a.extraDirectPay
                                        : !_.isNull(props.event?.extraDirectPay)
                                          ? props.event?.extraDirectPay!
                                          : false) && <CheckIcon />}
                                  </>,
                                  <>
                                    {(!_.isNull(ad.parkingDirectPay)
                                      ? ad.parkingDirectPay
                                      : !_.isNull(a.parkingDirectPay)
                                        ? a.parkingDirectPay
                                        : !_.isNull(props.event?.parkingDirectPay)
                                          ? props.event?.parkingDirectPay!
                                          : false) && <CheckIcon />}
                                  </>,
                                ];
                              }
                              return null;
                            })
                            .filter(r => r)
                            .map(r => r!)}
                        />
                      ))}
                    </SimpleAccordion>
                  </Grid>
                  <Grid item xs={12}>
                    <SimpleAccordion header={`${i18next.t('offer-event-comments')}`} autoOpen>
                      <SimpleTable
                        fullWidth={false}
                        headers={[
                          i18next.t('offer-event-comments-accomodation'),
                          i18next.t('offer-event-comments-payment'),
                          i18next.t('offer-event-comments-rooms'),
                          i18next.t('offer-event-comments-other'),
                        ]}
                        rows={[[props.event?.commentAccomodation, props.event?.commentPayment, props.event?.commentRooms, props.event?.commentOther]]}
                      />
                    </SimpleAccordion>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      sx={{ marginRight: 2 }}
                      variant="contained"
                      color="secondary"
                      startIcon={<DownloadIcon />}
                      onClick={() => {
                        window.open(getFullBackendUrl(`/backend/pdf/downloadChecklist/${props.event?.id}`), '_blank');
                      }}
                    >
                      {i18next.t('offer-event-tab-download')}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <AdminOfferDocumentViewer offerId={props.data.id} />
                  </Grid>
                </Grid>,
                <BookingTasksList
                  bookingId={props.data.bookingId ?? props.data.id}
                  bookingRefCode={props.data.refCode}
                  bookingTitle={props.version.title ?? undefined}
                  publicClientId={props.version.offer?.publicClient?.id}
                  publicCompanyId={props.data.publicCompany?.id}
                />,
                <Grid container>
                  {/* Only show event started/ended buttons in to Root Users */}
                  {!props.guestMode && user.isRoot && (
                    <Grid item xs={12}>
                      {versionType === EOfferVersionType.RESERVATION_CONFIRMATION &&
                        versionStatus === EOfferVersionStatus.APPROVED &&
                        offerStatus === EOfferStatus.APPROVED && (
                          <Button
                            variant="text"
                            onClick={async () => {
                              try {
                                await processEventStartedMutateFunction({
                                  variables: {
                                    offerVersionId: props.version.id,
                                  },
                                  refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
                                });
                                navigate(`/offers/${offerSource.toLowerCase()}`);
                              } catch (err) {
                                dispatchException(dispatch, err);
                              }
                            }}
                          >
                            {i18next.t('offer-process-eventstarted')}
                          </Button>
                        )}
                      {versionType === EOfferVersionType.RESERVATION_CONFIRMATION &&
                        versionStatus === EOfferVersionStatus.APPROVED &&
                        (offerStatus === EOfferStatus.APPROVED || offerStatus === EOfferStatus.ACTIVE) && (
                          <Button
                            variant="text"
                            onClick={async () => {
                              try {
                                await processEventEndedMutateFunction({
                                  variables: {
                                    offerVersionId: props.version.id,
                                  },
                                  refetchQueries: REFETCH_OFFER_QUERIES(props.data.id, props.version.id),
                                });
                                navigate(`/offers/${offerSource.toLowerCase()}`);
                              } catch (err) {
                                dispatchException(dispatch, err);
                              }
                            }}
                          >
                            {i18next.t('offer-process-eventended')}
                          </Button>
                        )}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <OfferHistoryTable
                      history={props.data.history}
                      emailHistory={props.data.emailHistory}
                      canSeeClientData={canSeeCustomerData}
                      onOfferVersionClick={setShowOfferVersionId}
                      onEmailResent={() => {
                        // Refetch the offer data to update the email history
                        REFETCH_OFFER_QUERIES(props.data.id, props.version.id).forEach(query => {
                          client.query({
                            ...query,
                            fetchPolicy: 'network-only',
                          });
                        });
                      }}
                    />
                  </Grid>
                </Grid>,
              ]}
            />
          </ContactPersonTabsWrapper>
        </Grid>
      </Grid>

      <AgbConfirmationDialog
        open={agbDialogOpen}
        title={_approveQuestionTitle()}
        isListerBooking={true}
        spaceId={props.data.space!.id}
        askSavePrices={isEditorDirty}
        isPremiumClient={props.data.process === 'PREMIUM'}
        hotelCountry={props.data.hotel.businessCountry}
        cancellationRules={cancellationRulesData?.viewWlOfferVersion?.offer?.cancellationRules || []}
        onClose={handleAgbDialogClose}
        onConfirm={handleAgbDialogConfirm}
      >
        {_approveQuestion()}
      </AgbConfirmationDialog>
      <CustomerCommentDialog
        open={customerCommentDialogOpen}
        comment={hotelMessage}
        files={requestFiles}
        onConfirm={() => setCustomerCommentDialogOpen(false)}
      />
      <OfferRejectionDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onSubmit={handleRejectSubmit}
        loading={submitRunning}
        offerStatus={props.data.status}
        versionStatus={props.version.status}
      />
      {guestSuccessDialog && (
        <InformationDialog
          open={true}
          title=""
          confirmButtonText={i18next.t('offer-guest-success-ok') as string}
          onConfirm={() => setGuestSuccessDialog(null)}
        >
          <Typography color={guestSuccessDialog === 'draft' ? 'text.primary' : undefined}>
            {guestSuccessDialog === 'approved' && i18next.t('offer-guest-success-approved')}
            {guestSuccessDialog === 'draft' && i18next.t('offer-guest-success-draft')}
            {guestSuccessDialog === 'declined' && i18next.t('offer-guest-success-declined')}
          </Typography>
        </InformationDialog>
      )}
    </>
  );

  if (props.guestMode) {
    return <GuestOfferUserContext.Provider value={user}>{content}</GuestOfferUserContext.Provider>;
  }
  return content;
}

/**
 * Offer detail page wrapper that loads offer and version data.
 * Event query errors are handled gracefully (event is optional).
 */
export default function Offer(props: OfferProps) {
  const bookingMode = bookingModeSelector();

  const offerQuery = useQuery(OFFER_AND_VERSION_VIEW_QUERY, {
    variables: { id: props.id, versionId: props.versionId },
    fetchPolicy: 'network-only',
  });
  const eventQuery = useQuery(EVENT_VIEW_QUERY, {
    variables: { offerId: props.id },
    fetchPolicy: 'network-only',
  });

  // Event data is optional for this page. A transient event-query network error
  // (e.g. Safari "Load failed") should not break the whole offer view.
  const loading = offerQuery.loading;
  const error = offerQuery.error;
  const event = eventQuery.error ? null : eventQuery.data?.viewWlOfferEvent || null;

  const offerSource = offerQuery.data?.viewWlOffer?.source;
  const modeRedirect = useBookingModeRedirect(bookingMode, offerSource);

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else if (!offerQuery.data?.viewWlOffer || !offerQuery.data?.viewWlOfferVersion) return <Navigate to={`/offers`} />;
  else if (modeRedirect) return <Navigate to={modeRedirect} replace />;
  else return <OfferForm data={offerQuery.data!.viewWlOffer!} version={offerQuery.data!.viewWlOfferVersion!} event={event} />;
}

/**
 * Wrapper component that loads the latest version of an offer.
 * Uses network-only fetch policy to ensure fresh data.
 */
export function OfferLatest(props: OfferLatestProps) {
  const bookingMode = props.guestMode ? null : bookingModeSelector();

  const offerQuery = useQuery(OFFER_VIEW_QUERY, {
    variables: { id: props.id },
    fetchPolicy: 'network-only',
  });
  const eventQuery = useQuery(EVENT_VIEW_QUERY, {
    variables: { offerId: props.id },
    fetchPolicy: 'network-only',
  });

  // Event data is optional for this page. A transient event-query network error
  // (e.g. Safari "Load failed") should not break the whole offer view.
  const loading = offerQuery.loading;
  const error = offerQuery.error;
  const event = eventQuery.error ? null : eventQuery.data?.viewWlOfferEvent || null;

  const offerSource = offerQuery.data?.viewWlOffer?.source;
  const modeRedirect = useBookingModeRedirect(bookingMode, offerSource);

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else if (!offerQuery.data?.viewWlOffer || !offerQuery.data?.viewWlOfferLatestVersion) {
    return props.guestMode ? (
      <Typography color="error" sx={{ p: 3 }}>
        {i18next.t('offer-guest-access-denied')}
      </Typography>
    ) : (
      <Navigate to={`/offers`} />
    );
  } else if (modeRedirect) return <Navigate to={modeRedirect} replace />;
  else
    return (
      <OfferForm
        data={offerQuery.data!.viewWlOffer!}
        version={offerQuery.data!.viewWlOfferLatestVersion!}
        event={event}
        guestMode={props.guestMode}
      />
    );
}
